import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import ToolHeader, { Spinner } from './ToolHeader';

const money = (cur, n) => `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (p) => new Date(`${p}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

export default function PayslipsPage() {
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState('');

  useEffect(() => { api.get('/staff-app/my-payslips').then((r) => setData(r.data)).catch(() => setData({ linked: false, payslips: [] })); }, []);

  const open = async (s) => {
    setOpening(s.payslip_id);
    const win = window.open('', '_blank');
    try {
      const res = await api.get(`/staff-app/my-payslips/${s.run_id}/${s.payslip_id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.location.href = url;
    } catch {
      win?.close();
      toast.error('Could not open this payslip');
    } finally { setOpening(''); }
  };

  return (
    <div data-testid="staff-app-payslips-page">
      <ToolHeader title="My payslips" subtitle={data?.employee_name || ''} />
      {!data ? <Spinner /> : !data.linked ? (
        <p className="px-6 py-16 text-center text-sm text-muted-foreground" data-testid="staff-app-payslips-unlinked">
          Your login isn't linked to an employee record yet. Ask HR to add your work email to your employee profile.
        </p>
      ) : data.payslips.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="staff-app-payslips-empty">No payslips issued yet.</p>
      ) : (
        <div className="p-4 space-y-2.5">
          {data.payslips.map((s) => (
            <div key={s.payslip_id} className="rounded-xl border border-border bg-card p-3.5" data-testid={`staff-app-payslip-${s.payslip_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">{monthLabel(s.period)}</p>
                  <p className={`text-[11px] font-semibold ${s.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>{s.status === 'paid' ? 'Paid' : 'Finalised — payment pending'}</p>
                </div>
                <p className="text-lg font-bold tabular-nums" data-testid={`staff-app-payslip-net-${s.payslip_id}`}>{money(s.currency, s.net_pay)}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2.5 text-[11px] text-muted-foreground">
                <span>Gross<br /><b className="text-foreground">{money(s.currency, s.gross_pay)}</b></span>
                <span>SSNIT<br /><b className="text-foreground">{money(s.currency, s.ssnit_employee)}</b></span>
                <span>PAYE<br /><b className="text-foreground">{money(s.currency, s.paye)}</b></span>
              </div>
              <button onClick={() => open(s)} disabled={!!opening} data-testid={`staff-app-payslip-open-${s.payslip_id}`}
                className="mt-3 w-full h-10 rounded-lg border border-border text-sm font-semibold flex items-center justify-center gap-2 hover:bg-muted">
                {opening === s.payslip_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} View payslip PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
